import { expected, QualityControlOutput, QualityControlSection, schQualityControlSection } from '@aloft/toolkit-shared';
import { Flex } from '@aloft/toolkit-sui';

import { ComponentProps, useEffect, useRef, useState } from 'react';

import { QualityControlReviewDetails } from './internal/QualityControlReviewDetails.tsx';
import { QualityControlReviewSidebar } from './internal/QualityControlReviewSidebar.tsx';

const sections = schQualityControlSection.options;

export const QualityControlReviewContent = ({
  updateQualityControl,
  ...props
}: Omit<
  ComponentProps<typeof QualityControlReviewSidebar>,
  'activeSection' | 'onSectionSelect'
> & {
  updateQualityControl: (body: QualityControlOutput) => Promise<QualityControlOutput>;
}) => {
  const [activeSection, setActiveSection] = useState<QualityControlSection>(expected(sections[0]));
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const sectionRefs = useRef<Partial<Record<QualityControlSection, HTMLDivElement | null>>>({});
  const isProgrammaticScrollRef = useRef(false);

  const handleScroll = () => {
    if (isProgrammaticScrollRef.current) return;

    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    const containerRect = scrollContainer.getBoundingClientRect();
    const { clientHeight, scrollHeight, scrollTop } = scrollContainer;

    const isAtBottom = scrollHeight - scrollTop - clientHeight <= 1;

    if (isAtBottom) {
      setActiveSection(expected(sections[sections.length - 1]));
      return;
    }

    const newActiveSection =
      sections
        .map((section) => ({ section, element: sectionRefs.current[section] }))
        .filter((entry): entry is { section: QualityControlSection; element: HTMLDivElement } =>
          Boolean(entry.element),
        )
        .map(({ section, element }) => ({ section, rect: element.getBoundingClientRect() }))
        .filter(({ rect }) => rect.top + rect.height / 4 >= containerRect.top)
        .map(({ section, rect }) => ({
          section,
          distance: Math.abs(rect.top - containerRect.top),
        }))
        .sort((a, b) => a.distance - b.distance)[0] ?? null;

    if (newActiveSection) {
      setActiveSection(newActiveSection.section);
    }
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    container?.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      container?.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleSectionSelect = (section: QualityControlSection) => {
    const scrollContainer = scrollContainerRef.current;
    const sectionElement = sectionRefs.current[section];
    if (!scrollContainer || !sectionElement) return;

    setActiveSection(section);
    isProgrammaticScrollRef.current = true;

    const containerRect = scrollContainer.getBoundingClientRect();
    const sectionRect = sectionElement.getBoundingClientRect();
    const scrollTop = scrollContainer.scrollTop + (sectionRect.top - containerRect.top);

    scrollContainer.scrollTo({ top: scrollTop, behavior: 'smooth' });

    const endProgrammaticScroll = () => {
      isProgrammaticScrollRef.current = false;
    };

    if ('onscrollend' in scrollContainer) {
      scrollContainer.addEventListener('scrollend', endProgrammaticScroll, { once: true });
      return;
    }

    window.setTimeout(endProgrammaticScroll, 500);
  };

  return (
    <Flex height="100%" width="100%" flex={1} overflow="hidden">
      <QualityControlReviewSidebar
        {...props}
        activeSection={activeSection}
        onSectionSelect={handleSectionSelect}
      />
      <QualityControlReviewDetails
        qualityControl={props.qualityControl}
        scrollContainerRef={scrollContainerRef}
        sectionRefs={sectionRefs}
        updateQualityControl={updateQualityControl}
      />
    </Flex>
  );
};
